<template>
    <div id="product">
        <Teaser :backgroundUrl="teaser.imageUrl" :gift-box-banner-visible="product.giftBoxBannerVisible" forceLoad />
        <LayoutWithAside over-teaser :sticky-aside="isStickyAside">
            <template v-slot:main>
                <ImageGalleryFloating v-if="showImageGallery" :images="productDetails.images" class="d-print-none" />

                <ProductRatingControls
                    v-if="$store.getters.isMainShop && !isValueVoucher"
                    :product-id="productDetails.id"
                    :product-category="productDetails.productCategory"
                    class="py-3 ml-auto"
                />

                <DetailsSection>
                    <ProductHeader
                        :title="productDetails.title"
                        :bannerText="productDetails.bannerText"
                        :bannerBackgroundColor="productDetails.bannerBackgroundColor"
                        :hotel-details="hotelDetails"
                    />
                </DetailsSection>

                <DetailsSection v-if="showFeatures">
                    <FeatureIcons :features="productDetails.hotelTags" />
                </DetailsSection>

                <DetailsSection v-if="productDetails.additionalDescription">
                    <AdditionalDescription :descriptionHtml="productDetails.additionalDescription" />
                </DetailsSection>

                <DetailsSection v-if="showServices">
                    <template v-slot:header>
                        {{ servicesSectionHeader }}
                    </template>

                    <CheckList :items="productDetails.services" />
                </DetailsSection>

                <DetailsSection v-if="showVoucherInfos">
                    <template v-slot:header>
                        {{ voucherInfosSectionHeader }}
                    </template>

                    <TextList :items="productDetails.voucherInfos" />
                </DetailsSection>

                <NewsletterSection source="ProductDetailsPage" />

                <DetailsSection v-if="showHotelDescription">
                    <CollapsibleItem prepend-icon nested>
                        <template v-slot:summary.title>
                            <SectionHeader>
                                {{ hotelDescriptionSectionHeader }}
                            </SectionHeader>
                        </template>

                        <template v-slot:expandable>
                            <Description :descriptionHtml="productDetails.hotelDescription" />
                        </template>
                    </CollapsibleItem>
                </DetailsSection>

                <DetailsSection v-if="productDetails.description">
                    <CollapsibleItem prepend-icon nested>
                        <template v-slot:summary.title>
                            <SectionHeader>{{ $t('ProductDetails.section.aboutOffer') }}</SectionHeader>
                        </template>
                        <template v-slot:expandable>
                            <Description :descriptionHtml="productDetails.description" />
                        </template>
                    </CollapsibleItem>
                </DetailsSection>

                <DetailsSection v-if="showFacilities">
                    <CollapsibleItem prepend-icon nested>
                        <template v-slot:summary.title>
                            <SectionHeader>{{ $t('ProductDetails.section.featuresAndAmenities') }}</SectionHeader>
                        </template>
                        <template v-slot:expandable>
                            <Facilities
                                :extraServices="productDetails.extraServices"
                                :additionalHotelInfos="productDetails.additionalHotelInfos"
                                :facilityTags="productDetails.facilityTags"
                            />
                        </template>
                    </CollapsibleItem>
                </DetailsSection>

                <DetailsSection>
                    <template v-if="!isOtherProduct && showMap" #header
                        >{{ $t('ProductDetails.section.map') }}
                    </template>
                    <template v-else #header>{{ $t('HotelAddress.term.provider') }}</template>

                    <HotelMap v-if="showMap" :center="productDetails.latLon" :zoom="9" :markers="[marker]" />

                    <h6 v-if="!showMap && !isOtherProduct" class="mb-2">Animod GmbH</h6>

                    <HotelAddress :hotelDetails="hotelDetails" :is-other-product="isOtherProduct"></HotelAddress>
                </DetailsSection>

                <DetailsSection class="d-print-none" v-if="$store.state.ident !== 'www.animod.nl'">
                    <template #header>{{ $t('ProductDetails.section.social') }}</template>
                    <Share :title="productDetails.title" :pinterestImage="productDetails.teaserImageUrl"></Share>
                </DetailsSection>
            </template>

            <template v-slot:aside>

                <template v-if="$store.state.ident === 'mivo' && productDetails.auctionOffer">
                    <media-only desktop>
                        <MivoProductSelect
                            :productDetails="productDetails"
                            :buyerProtectionEnabled="product.buyerProtectionEnabled"
                        />
                    </media-only>
                </template>

                <ProductSelect v-else
                    :productDetails="productDetails"
                    :buyerProtectionEnabled="product.buyerProtectionEnabled"
                    :has-other-hotel-products="hasHotelProducts"
                >
                    <template v-if="$store.state.ident === '2fuer1'" #header>
                        <TwoForOneProductSelectHeader />
                    </template>
                    <template v-if="$store.state.ident === 'select'" #header>
                        <TwoForOneProductSelectHeader />
                    </template>
                    <template v-else-if="$store.state.ident === 'douglas'" #header>
                        <DouglasProductSelectHeader />
                    </template>
                    <template v-else-if="$store.state.ident === 'soliver'" #header>
                        <SOliverProductSelectHeader />
                    </template>
                    <template v-else-if="$store.state.ident === 'comma'" #header>
                        <CommaProductSelectHeader />
                    </template>
                    <template v-else-if="$store.state.ident === 'liebeskind'" #header>
                        <LiebeskindProductSelectHeader />
                    </template>
                    <template v-else-if="$store.state.ident === 'bofrost'" #header>
                        <BofrostProductSelectHeader />
                    </template>
                </ProductSelect>
            </template>

            <template v-if="!isOtherProduct" v-slot:after>
                <!-- grid first bool property is disabled to enable lazy loading  -->
                <Grid v-if="hasHotelProducts" :grid="hotelProducts" class="d-print-none mt-3" id="otherHotelProducts" />

                <DetailsSection v-if="relatedProducts">
                    <Grid :grid="relatedProducts" class="d-print-none mt-3" />
                </DetailsSection>

                <DetailsSection v-if="productDetails.faqContent">
                    <template #header>{{ $t('ProductDetails.section.faq') }}</template>
                    <Accordion :data="productDetails.faqContent" filled-titles />
                </DetailsSection>
            </template>
        </LayoutWithAside>
    </div>
</template>

<script>
import Teaser from '@/components/teasers/Teaser';
import FeatureIcons from '@/components/FeatureIcons';
import DetailsSection from '@/components/DetailsSection';
import AdditionalDescription from '@/components/product/AdditionalDescription';
import Description from '@/components/Description';
import CheckList from '@/components/product/CheckList';
import TextList from '@/components/product/TextList';
import DotList from '@/components/product/DotList';
import Facilities from '@/components/Facilities';
import HotelAddress from '@/components/HotelAddress';
import ProductSelect from '@/components/product/selection/ProductSelect.vue';
import LayoutWithAside from '@/components/LayoutWithAside';
import Grid from '@/components/grid/Grid';
import productDetails from '@/mixins/productDetails';
import SectionHeader from '@/components/SectionHeader';
import NewsletterSection from '@/components/newsletter/NewsletterSection';
import ProductHeader from '@/components/product/ProductHeader';
import ImageGalleryFloating from '@/components/product/ImageGalleryFloating';
import CollapsibleItem from '@/components/utility/CollapsibleItem';
import Accordion from '@/components/grid/Accordion';
import ProductRatingControls from '@/components/product/rating/ProductRatingControls';

export default {
    name: 'ProductDetailsDesktop',
    mixins: [productDetails],
    components: {
        Accordion,
        ProductHeader,
        NewsletterSection,
        SectionHeader,
        CollapsibleItem,
        FeatureIcons,
        DetailsSection,
        AdditionalDescription,
        Description,
        CheckList,
        TextList,
        Facilities,
        HotelAddress,
        ProductSelect,
        Teaser,
        LayoutWithAside,
        Grid,
        ImageGalleryFloating,
        ProductRatingControls,
        DotList, // used in nvw
        //async components
        Share: () => import(/* webpackChunkName: "share" */ '@/components/Share'),
        HotelMap: () => import(/* webpackChunkName: "hotel-map" */ '@/components/map/HotelMap'),
        TwoForOneProductSelectHeader: () =>
            import(/* webpackChunkName: "2-for-1" */ '@/themes/2fuer1/components/TwoForOneProductSelectHeader'),
        DouglasProductSelectHeader: () =>
            import(/* webpackChunkName: "douglas" */ '@/themes/douglas/components/DouglasProductSelectHeader'),
        SOliverProductSelectHeader: () =>
            import(/* webpackChunkName: "soliver" */ '@/themes/soliver/components/SOliverProductSelectHeader'),
        CommaProductSelectHeader: () =>
            import(/* webpackChunkName: "comma" */ '@/themes/comma/components/CommaProductSelectHeader'),
        LiebeskindProductSelectHeader: () =>
            import(/* webpackChunkName: "liebeskind" */ '@/themes/liebeskind/components/LiebeskindProductSelectHeader'),
        BofrostProductSelectHeader: () =>
            import(/* webpackChunkName: "bofrost" */ '@/themes/bofrost/components/BofrostProductSelectHeader'),
        MivoProductSelect: () =>
            import(/* webpackChunkName: "mivo-product-select" */ '@/themes/mivo/components/product/MivoProductSelect'),
    },
    computed: {
        isStickyAside() {
            // should be sticky if aside is more than 100vh, but probably with ResizeObserver on aside? May lead to jumps
            // aside.scrollHeight > aside.clientHeight => sticky off
            const isDirectBooking = !!(this.productDetails.variantsWithScheduledArrival || []).length;
            const isNotFormView = this.productDetails.variantSelectionMode !== 'FormView';
            const hasFewVariants = (this.productDetails.variants || []).length <= 2;
            return isNotFormView && !isDirectBooking && hasFewVariants;
        }
    }
};
</script>

<style scoped lang="scss">
#product {
    font-size: 1rem;

    .product-teaser ::v-deep .teaser-text {
        padding-right: calc(15px + 360px);
    }
}
</style>
